import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ShieldCheck, AlertCircle } from "lucide-react";

const API = '/api';

const SCOPE_TYPES = [
  { value: "sender", label: "Sender Email" },
  { value: "department", label: "Department" },
  { value: "shared_mailbox", label: "Shared Mailbox" },
  { value: "domain", label: "Domain" },
];

const ACTION_TYPES = [
  { value: "force_template", label: "Force Template" },
  { value: "skip", label: "Skip Signature" },
  { value: "force_mode", label: "Force Mode (FULL/SHORT)" },
  { value: "force_brand", label: "Use Brand" },
  { value: "exclude_banner", label: "Exclude Banner" },
];

const SCOPE_BADGE = {
  sender: "bg-[#0F6CBD] text-white",
  department: "bg-[#107C10] text-white",
  shared_mailbox: "bg-[#D83B01] text-white",
  domain: "bg-[#5E5E5E] text-white",
};

const emptyForm = {
  scope_type: "sender", scope_value: "", action_type: "force_template",
  action_value: "", priority: 100, active: true, description: "",
};

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [templates, setTemplates] = useState([]);
  const [brands, setBrands] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const [rRes, tRes, bRes] = await Promise.all([
      axios.get(`${API}/rules/`),
      axios.get(`${API}/templates/`),
      axios.get(`${API}/brands/`),
    ]);
    setRules(rRes.data.rules);
    setTemplates(tRes.data.templates);
    setBrands(bRes.data.brands);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => { setForm({ ...emptyForm }); setEditingId(null); setShowForm(true); };
  const openEdit = (rule) => {
    setForm({
      scope_type: rule.scope_type, scope_value: rule.scope_value,
      action_type: rule.action_type, action_value: rule.action_value || "",
      priority: rule.priority, active: rule.active, description: rule.description || "",
    });
    setEditingId(rule.id);
    setShowForm(true);
  };

  const save = async () => {
    if (editingId) {
      await axios.put(`${API}/rules/${editingId}`, form);
    } else {
      await axios.post(`${API}/rules/`, form);
    }
    setShowForm(false);
    toast.success(editingId ? "Rule updated" : "Rule created");
    fetchData();
  };

  const remove = async (id) => {
    await axios.delete(`${API}/rules/${id}`);
    toast.success("Rule deleted");
    fetchData();
  };

  const toggleActive = async (rule) => {
    await axios.put(`${API}/rules/${rule.id}`, { active: !rule.active });
    fetchData();
  };

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const needsValue = ["force_template", "force_mode", "force_brand"].includes(form.action_type);

  return (
    <div className="p-8 max-w-[1600px]" data-testid="rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Rules & Exceptions</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">Override signature behavior for specific senders, departments, or domains</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm" data-testid="create-rule-btn">
          <Plus size={14} className="mr-2" /> New Rule
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mt-6 bg-white border border-[#0F6CBD] rounded-sm p-5" data-testid="rule-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base font-medium text-[#171717]">{editingId ? "Edit Rule" : "New Rule"}</h2>
            <button onClick={() => setShowForm(false)} className="text-[#5E5E5E] hover:text-[#171717]"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Scope Type</label>
              <select value={form.scope_type} onChange={(e) => upd("scope_type", e.target.value)} className="w-full border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm" data-testid="rule-scope-type">
                {SCOPE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Scope Value</label>
              <Input value={form.scope_value} onChange={(e) => upd("scope_value", e.target.value)} placeholder={form.scope_type === "sender" ? "user@company.com" : form.scope_type === "department" ? "Sales" : form.scope_type === "domain" ? "company.com" : "mailbox@company.com"} className="border-[#E1DFDD] rounded-sm" data-testid="rule-scope-value" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Action</label>
              <select value={form.action_type} onChange={(e) => upd("action_type", e.target.value)} className="w-full border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm" data-testid="rule-action-type">
                {ACTION_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            {needsValue && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">
                  {form.action_type === "force_template" ? "Template" : form.action_type === "force_mode" ? "Mode" : "Brand"}
                </label>
                {form.action_type === "force_template" ? (
                  <select value={form.action_value} onChange={(e) => upd("action_value", e.target.value)} className="w-full border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm" data-testid="rule-action-value">
                    <option value="">-- Select --</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.mode})</option>)}
                  </select>
                ) : form.action_type === "force_mode" ? (
                  <select value={form.action_value} onChange={(e) => upd("action_value", e.target.value)} className="w-full border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm" data-testid="rule-action-value">
                    <option value="FULL">FULL</option>
                    <option value="SHORT">SHORT</option>
                  </select>
                ) : (
                  <select value={form.action_value} onChange={(e) => upd("action_value", e.target.value)} className="w-full border border-[#E1DFDD] rounded-sm px-3 py-2 text-sm" data-testid="rule-action-value">
                    <option value="">-- Select --</option>
                    {brands.map((b) => <option key={b.id} value={b.id}>{b.display_name}</option>)}
                  </select>
                )}
              </div>
            )}
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Priority (lower = higher)</label>
              <Input type="number" value={form.priority} onChange={(e) => upd("priority", parseInt(e.target.value) || 0)} className="border-[#E1DFDD] rounded-sm" data-testid="rule-priority" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1 block">Description</label>
              <Input value={form.description} onChange={(e) => upd("description", e.target.value)} placeholder="Optional note" className="border-[#E1DFDD] rounded-sm" data-testid="rule-description" />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={save} className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm" data-testid="save-rule-btn">{editingId ? "Update" : "Create"} Rule</Button>
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-[#E1DFDD] rounded-sm">Cancel</Button>
          </div>
        </div>
      )}

      {/* Priority info */}
      <div className="mt-4 bg-[#EBF3FC] border border-[#0F6CBD]/20 rounded-sm p-3 flex items-start gap-2">
        <AlertCircle size={14} className="text-[#0F6CBD] mt-0.5 shrink-0" />
        <p className="text-[11px] text-[#5E5E5E]">
          <span className="font-medium text-[#171717]">Priority order:</span> Explicit sender &gt; Shared mailbox &gt; Department &gt; Domain/brand default &gt; Tenant default. Lower priority number = evaluated first.
        </p>
      </div>

      {/* Rules list */}
      {loading ? (
        <p className="text-sm text-[#5E5E5E] mt-6">Loading rules...</p>
      ) : rules.length === 0 ? (
        <div className="mt-6 text-center py-12 border border-dashed border-[#E1DFDD] rounded-sm">
          <ShieldCheck size={32} className="mx-auto text-[#E1DFDD]" />
          <p className="text-sm text-[#5E5E5E] mt-3">No rules configured. All senders use default brand templates.</p>
        </div>
      ) : (
        <div className="mt-4 bg-white border border-[#E1DFDD] rounded-sm overflow-hidden" data-testid="rules-table">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="text-xs uppercase bg-[#FAFAFA] text-[#171717] border-b border-[#E1DFDD]">
                <th className="px-4 py-3 font-semibold w-8">P</th>
                <th className="px-4 py-3 font-semibold">Scope</th>
                <th className="px-4 py-3 font-semibold">Value</th>
                <th className="px-4 py-3 font-semibold">Action</th>
                <th className="px-4 py-3 font-semibold">Description</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} className={`border-b border-[#E1DFDD] hover:bg-[#FAFAFA] ${!rule.active ? "opacity-50" : ""}`} data-testid={`rule-row-${rule.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-[#5E5E5E]">{rule.priority}</td>
                  <td className="px-4 py-3">
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${SCOPE_BADGE[rule.scope_type] || "bg-[#F3F2F1] text-[#5E5E5E]"}`}>
                      {rule.scope_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#171717]">{rule.scope_value}</td>
                  <td className="px-4 py-3 text-xs text-[#5E5E5E]">
                    {rule.action_type.replace(/_/g, " ")}
                    {rule.action_value && <span className="ml-1 font-mono text-[#0F6CBD]">= {rule.action_value}</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#5E5E5E] max-w-[200px] truncate">{rule.description || "-"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(rule)} className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold cursor-pointer ${rule.active ? "bg-[#107C10] text-white" : "bg-[#E1DFDD] text-[#5E5E5E]"}`} data-testid={`toggle-rule-${rule.id}`}>
                      {rule.active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(rule)} className="p-1 text-[#5E5E5E] hover:text-[#0F6CBD] hover:bg-[#EBF3FC] rounded-sm" data-testid={`edit-rule-${rule.id}`}>
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => remove(rule.id)} className="p-1 text-[#5E5E5E] hover:text-[#A4262C] hover:bg-red-50 rounded-sm" data-testid={`delete-rule-${rule.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
